import { useEffect, useState } from "react";
import { toast } from "sonner";
import { MessageCircle, Send, ShieldCheck } from "lucide-react";
import { http } from "../lib/api";
import { visitorId } from "./LikeButton";

const NAME_KEY = "bpv_comment_name";
const inp = "w-full border border-slate-800 bg-[#0d0c11] px-4 py-3 text-sm text-white outline-none transition-colors focus:border-rose-500";

export const Comments = ({ mediaId }) => {
  const [list, setList] = useState([]);
  const [name, setName] = useState(() => localStorage.getItem(NAME_KEY) || "");
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    setSent(false);
    http.get(`/media/${mediaId}/comments`).then((r) => setList(Array.isArray(r.data) ? r.data : [])).catch(() => setList([]));
  }, [mediaId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || text.trim().length < 2) return toast.error("Escreva seu nome e o comentário");
    setSending(true);
    try {
      await http.post(`/media/${mediaId}/comments`, { name: name.trim(), text: text.trim(), visitor_id: visitorId() });
      localStorage.setItem(NAME_KEY, name.trim());
      setText("");
      setSent(true);
      toast.success("Comentário enviado! Ele aparece depois de aprovado.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Falha ao enviar comentário");
    } finally {
      setSending(false);
    }
  };

  return (
    <section className="mt-16 max-w-3xl" data-testid="comments-section">
      <h2 className="font-serif-d mb-8 flex items-center gap-3 text-3xl text-white">
        <MessageCircle className="text-rose-500" /> Comentários <span className="font-mono-d text-sm text-slate-400" data-testid="comments-count">({list.length})</span>
      </h2>
      <form onSubmit={submit} className="space-y-3" data-testid="comment-form">
        <input value={name} onChange={(e) => setName(e.target.value)} maxLength={40} placeholder="Seu nome ou apelido" className={inp} data-testid="comment-name-input" />
        <textarea value={text} onChange={(e) => setText(e.target.value)} maxLength={1000} rows={3} placeholder="Escreva um comentário..." className={inp} data-testid="comment-text-input" />
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-xs text-slate-400">Comentários passam por aprovação antes de aparecer.</p>
          <button disabled={sending} className="flex items-center gap-2 bg-rose-600 px-5 py-3 text-xs font-semibold uppercase tracking-[0.18em] text-white hover:bg-rose-500 disabled:opacity-50" data-testid="comment-submit-button">
            <Send size={14} /> {sending ? "Enviando..." : "Comentar"}
          </button>
        </div>
        {sent && <p className="border border-emerald-800/60 bg-emerald-950/40 p-3 text-sm text-emerald-200" data-testid="comment-pending-notice">Recebemos seu comentário. Ele será publicado após a aprovação do administrador.</p>}
      </form>
      <ul className="mt-10 space-y-6" data-testid="comments-list">
        {list.length === 0 && <li className="text-sm text-slate-400">Seja o primeiro a comentar.</li>}
        {list.map((c) => (
          <li key={c.id} className="border-t border-white/5 pt-5" data-testid={`comment-${c.id}`}>
            <p className="flex items-baseline gap-3">
              <span className="font-medium text-white">{c.name}</span>
              <span className="font-mono-d text-xs text-slate-400">{new Date(c.created_at).toLocaleDateString("pt-BR")}</span>
            </p>
            <p className="mt-2 whitespace-pre-line leading-relaxed text-slate-300">{c.text}</p>
            {c.reply?.text && (
              <div className="ml-4 mt-4 border-l-2 border-rose-600 bg-[#0d0c11] p-4" data-testid={`comment-reply-${c.id}`}>
                <p className="flex items-center gap-2">
                  <ShieldCheck size={14} className="text-rose-400" />
                  <span className="text-sm font-medium text-white">Equipe do site</span>
                  <span className="font-mono-d text-xs text-slate-400">{new Date(c.reply.created_at).toLocaleDateString("pt-BR")}</span>
                </p>
                <p className="mt-2 whitespace-pre-line text-sm leading-relaxed text-slate-300">{c.reply.text}</p>
              </div>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
};
